/**
 * Session-scoped token storage for the auth flow.
 *
 * Tokens live in `sessionStorage`: they survive navigation within the tab but
 * are cleared when it closes. All access is guarded so this module is safe to
 * import anywhere (every getter returns `null` when storage is unavailable).
 *
 * Keys:
 * - `accessToken` — the JWT. After `otp/verify` it authorises MFA setup; after
 *   `mfa/verify` it is the full session token.
 * - `mfaToken`    — short-lived, single-use token from `check-email`.
 * - `otpauthUrl`  — `otpauth://` URI from `mfa/setup`, read by the manual-key step.
 * - `pendingEmail`— the address an email OTP was sent to (for resend).
 */

const ACCESS_TOKEN_KEY = 'amliq.accessToken'
const MFA_TOKEN_KEY = 'amliq.mfaToken'
const OTPAUTH_URL_KEY = 'amliq.otpauthUrl'
const PENDING_EMAIL_KEY = 'amliq.pendingEmail'

function read(key: string): string | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(key, value)
  } catch {
    /* storage unavailable (private mode / quota) — fail soft */
  }
}

function remove(key: string): void {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(key)
  } catch {
    /* fail soft */
  }
}

export const tokenStore = {
  getAccessToken: () => read(ACCESS_TOKEN_KEY),
  setAccessToken: (token: string) => write(ACCESS_TOKEN_KEY, token),
  clearAccessToken: () => remove(ACCESS_TOKEN_KEY),

  getMfaToken: () => read(MFA_TOKEN_KEY),
  setMfaToken: (token: string) => write(MFA_TOKEN_KEY, token),
  clearMfaToken: () => remove(MFA_TOKEN_KEY),

  getOtpauthUrl: () => read(OTPAUTH_URL_KEY),
  setOtpauthUrl: (url: string) => write(OTPAUTH_URL_KEY, url),

  getPendingEmail: () => read(PENDING_EMAIL_KEY),
  setPendingEmail: (email: string) => write(PENDING_EMAIL_KEY, email),

  /** Wipe all auth state — call on sign-out or a fatal session error. */
  clear: () => {
    ;[ACCESS_TOKEN_KEY, MFA_TOKEN_KEY, OTPAUTH_URL_KEY, PENDING_EMAIL_KEY].forEach(remove)
  },
}
